// src/hooks/useClosedTasks.ts
// CLOSED 任務的分頁管理 Hook
// 不使用 realtime，不寫入 IndexedDB，切到 CLOSED tab 時才按需載入

import { useState, useCallback, useRef } from 'react';
import { Task } from '../types/task';
import { taskService } from '../features/task';
import { logError } from '../utils/logger';
import { DocumentSnapshot } from 'firebase/firestore';

interface UseClosedTasksReturn {
  tasks: Task[];
  isLoading: boolean;
  hasMore: boolean;
  load: () => Promise<void>;
  loadMore: () => Promise<void>;
  prependTask: (task: Task) => void;
  removeTask: (taskId: string) => void;
  reset: () => void;
}

export const useClosedTasks = (userId: string): UseClosedTasksReturn => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<DocumentSnapshot | null>(null);
  const [initialized, setInitialized] = useState(false);
  const needsRefetchRef = useRef(false);

  const load = useCallback(async () => {
    if ((initialized && !needsRefetchRef.current) || isLoading) return;

    needsRefetchRef.current = false;
    setIsLoading(true);
    try {
      const result = await taskService.getClosedTasksPaginated(userId);
      setTasks(result.tasks);
      setHasMore(result.hasMore);
      setLastDoc(result.lastDoc);
      setInitialized(true);
    } catch (error) {
      logError('載入 CLOSED 任務失敗', error);
    } finally {
      setIsLoading(false);
    }
  }, [userId, initialized, isLoading]);

  const loadMore = useCallback(async () => {
    if (!hasMore || isLoading || !lastDoc) return;

    setIsLoading(true);
    try {
      const result = await taskService.getClosedTasksPaginated(userId, {
        startAfterDoc: lastDoc,
      });
      setTasks((prev: Task[]) => [...prev, ...result.tasks]);
      setHasMore(result.hasMore);
      setLastDoc(result.lastDoc);
    } catch (error) {
      logError('載入更多 CLOSED 任務失敗', error);
    } finally {
      setIsLoading(false);
    }
  }, [userId, hasMore, isLoading, lastDoc]);

  // 樂觀更新：OPEN→CLOSED 時把 task 插到最前面
  const prependTask = useCallback((task: Task) => {
    setTasks(prev => [task, ...prev]);
  }, []);

  // 樂觀更新：CLOSED→OPEN 時從列表移除
  const removeTask = useCallback((taskId: string) => {
    setTasks(prev => prev.filter(t => t.id !== taskId));
  }, []);

  const reset = useCallback(() => {
    needsRefetchRef.current = true;
  }, []);

  return { tasks, isLoading, hasMore, load, loadMore, prependTask, removeTask, reset };
};
